import { useMemo } from 'react';
import {
  MembershipTier,
  TierLimits,
  TIER_LIMITS,
  UserProfile,
  isWithinLimit,
  canAccessTier,
} from '../types';
import { resolveLiveModeFromProfile } from './useLiveTradingMode';

export type TierFeature = keyof TierLimits;
export type AiFeature = 'NEWS_ANALYSIS' | 'PORTFOLIO_ADVICE' | 'STOCK_ANALYSIS' | 'OPTIMIZATION';

const AI_FEATURE_LIMIT_KEY: Record<AiFeature, keyof TierLimits> = {
  NEWS_ANALYSIS:    'newsAnalysisPerMonth',
  PORTFOLIO_ADVICE: 'portfolioAdvicePerMonth',
  STOCK_ANALYSIS:   'stockAnalysisPerMonth',
  OPTIMIZATION:     'optimizationPerMonth',
};

interface TierGateResult {
  /** 현재 멤버십 등급 */
  tier: MembershipTier;
  /** 현재 등급의 전체 한도 설정 */
  limits: TierLimits;

  /**
   * boolean 기능 (canExportCSV 등)이 현재 등급에서 허용되는지 확인
   */
  checkFeature: (feature: TierFeature) => boolean;

  /**
   * 특정 AI 기능의 사용량이 한도 내인지 확인
   * @param feature  AI 기능 종류
   * @param usedCount 이번 달 사용 횟수
   */
  checkUsageLimit: (feature: AiFeature, usedCount: number) => boolean;

  /**
   * 특정 AI 기능의 남은 사용 횟수 반환 (-1 = 무제한)
   */
  remainingUsage: (feature: AiFeature, usedCount: number) => number;

  /**
   * 보유 종목 추가 가능 여부
   */
  canAddHolding: (currentCount: number) => boolean;

  /**
   * 관심종목 추가 가능 여부
   */
  canAddWatchlist: (currentCount: number) => boolean;

  /**
   * 요구 등급에 접근 가능한지 여부
   */
  hasAccess: (requiredTier: MembershipTier) => boolean;

  /** 업그레이드가 필요한 다음 등급 */
  nextTier: MembershipTier | null;

  /** 등급 만료 여부 */
  isExpired: boolean;

  /** 등급 한국어 레이블 */
  tierLabel: string;

  /** 퀀트 시그널 웹훅 부가상품 접근 권한 여부 */
  hasAutoTradingAccess: boolean;
  /** AI 개인화 및 모델 설정 부가상품 접근 권한 여부 */
  hasAiPersonalizationAccess: boolean;
  /** Chat Plus (AI 예측 및 대화형 퀀트 지원) 부가상품 접근 권한 여부 */
  hasChatPlusAccess: boolean;
  /** 시그널 웹훅 부가상품 접근 권한 여부 */
  hasAgentAddonAccess: boolean;
}

const TIER_LABELS: Record<MembershipTier, string> = {
  TIER1: 'Tier 1 (주린이)',
  TIER2: 'Tier 2 (새싹 투자자)',
  TIER3: 'Tier 3 (시장 참여자)',
  TIER4: 'Tier 4 (전략가)',
  TIER5: 'Tier 5 (프로)',
};

const NEXT_TIER: Record<MembershipTier, MembershipTier | null> = {
  TIER1: 'TIER2',
  TIER2: 'TIER3',
  TIER3: 'TIER4',
  TIER4: 'TIER5',
  TIER5: null,
};

export function useTierGate(userProfile: UserProfile | null): TierGateResult {
  const tier = useMemo<MembershipTier>(() => {
    const raw = userProfile?.membershipTier as string;
    
    // Legacy support for unmigrated data
    if (raw === 'FREE') return 'TIER1';
    if (raw === 'PRO') return 'TIER3';
    if (raw === 'PREMIUM') return 'TIER5';
    
    // Support new TIER_X format
    const normalized = raw?.replace('_', '');
    
// eslint-disable-next-line
    if (normalized && TIER_LIMITS.hasOwnProperty(normalized)) return normalized as MembershipTier;
    return 'TIER1';
  }, [userProfile?.membershipTier]);

  const limits = useMemo(() => TIER_LIMITS[tier], [tier]);

  const isExpired = useMemo(() => {
    if (!userProfile?.tierExpiresAt) return false;
    return new Date(userProfile.tierExpiresAt) < new Date();
  }, [userProfile?.tierExpiresAt]);

  // 만료된 경우 TIER1 한도 적용
  const effectiveLimits = useMemo(
    () => (isExpired && tier !== 'TIER1' ? TIER_LIMITS.TIER1 : limits),
    [isExpired, limits, tier]
  );

  const checkFeature = (feature: TierFeature): boolean => {
    if (feature === 'canCustomizePrompts') {
      return hasAiPersonalizationAccess;
    }
    const val = effectiveLimits[feature];
    if (typeof val === 'boolean') return val;
    return val === -1 || (typeof val === 'number' && val > 0);
  };

  const checkUsageLimit = (feature: AiFeature, usedCount: number): boolean => {
    const limitKey = AI_FEATURE_LIMIT_KEY[feature];
    const limit = effectiveLimits[limitKey] as number;
    return isWithinLimit(limit, usedCount);
  };

  const remainingUsage = (feature: AiFeature, usedCount: number): number => {
    const limitKey = AI_FEATURE_LIMIT_KEY[feature];
    const limit = effectiveLimits[limitKey] as number;
    if (limit === -1) return -1;
    return Math.max(0, limit - usedCount);
  };

  const canAddHolding = (currentCount: number): boolean => {
    if (effectiveLimits.canAddHoldings === false) return false;
    return isWithinLimit(effectiveLimits.holdingsLimit, currentCount);
  };

  const canAddWatchlist = (currentCount: number): boolean =>
    isWithinLimit(effectiveLimits.watchlistLimit, currentCount);

  const hasAccess = (requiredTier: MembershipTier): boolean => {
    if (isExpired && tier !== 'TIER1') return canAccessTier('TIER1', requiredTier);
    return canAccessTier(tier, requiredTier);
  };

  const isLiveMode = useMemo(() => resolveLiveModeFromProfile(userProfile), [userProfile]);

  const hasAutoTradingAccess = useMemo(() => {
    const addon = userProfile?.activeAddons?.find(a =>
      isLiveMode
        ? (a.id === 'addon_smart_order_standard' ||
           a.id === 'addon_smart_order_pro' ||
           a.id === 'addon_smart_order_vip')
        : (a.id === 'addon_agent_standard' ||
           a.id === 'addon_agent_pro' ||
           a.id === 'addon_agent_vip')
    );
    if (!addon) return false;
    if (!addon.expiresAt) return true;
    return new Date(addon.expiresAt) >= new Date();
  }, [userProfile?.activeAddons, isLiveMode]);

  const hasAiPersonalizationAccess = useMemo(() => {
    const addon = userProfile?.activeAddons?.find(a => a.id === 'AI_PERSONALIZATION_ADDON');
    if (!addon) return false;
    if (!addon.expiresAt) return true;
    return new Date(addon.expiresAt) >= new Date();
  }, [userProfile?.activeAddons]);

  const hasChatPlusAccess = useMemo(() => {
    const addon = userProfile?.activeAddons?.find(a => a.id === 'CHAT_PREDICTION_ADDON');
    if (!addon) return false;
    if (!addon.expiresAt) return true;
    return new Date(addon.expiresAt) >= new Date();
  }, [userProfile?.activeAddons]);

  const hasAgentAddonAccess = useMemo(() => {
    return !!(userProfile?.activeAddons?.some(a =>
      isLiveMode
        ? (a.id === 'addon_smart_order_standard' ||
           a.id === 'addon_smart_order_pro' ||
           a.id === 'addon_smart_order_vip')
        : (a.id === 'addon_agent_standard' ||
           a.id === 'addon_agent_pro' ||
           a.id === 'addon_agent_vip')
    ));
  }, [userProfile?.activeAddons, isLiveMode]);

  return {
    tier,
    limits: effectiveLimits,
    checkFeature,
    checkUsageLimit,
    remainingUsage,
    canAddHolding,
    canAddWatchlist,
    hasAccess,
    nextTier: NEXT_TIER[tier],
    isExpired,
    tierLabel: TIER_LABELS[tier],
    hasAutoTradingAccess,
    hasAiPersonalizationAccess,
    hasChatPlusAccess,
    hasAgentAddonAccess,
  };
}
